import {Table} from 'antd';
import {RightOutlined} from '@ant-design/icons';
import {
  UNCATEGORISED,
  formatAverageRating,
  formatChange,
  formatCount,
  formatCoverage,
  formatDuration,
  languageLabel,
  productLabel,
} from './performanceUtils';
import {Explained, TERMS} from './Info';
import styles from './performance.module.scss';

// Tickets with no category/feature/issue come back as a row with a null id.
// Filtering by that row means sending UNCATEGORISED.
export const rankingFilterId = row => row?.id ?? UNCATEGORISED;

export const rankingLabel = row =>
  row?.label ||
  (row?.id && row.id !== UNCATEGORISED ? row.id : 'Not categorised');

const changeClass = change => {
  if (change === 'New') return styles.changeNew;
  if (change.startsWith('+')) return styles.changeUp;
  if (change.startsWith('−')) return styles.changeDown;
  return styles.changeFlat;
};

/**
 * Top ten of a ranking. Counts are tickets *opened* in the period — or
 * *resolved*, with `basis="RESOLVED"`; the share
 * is of everything opened, not of the ten shown. Rating and turnaround
 * describe each row's *resolved* tickets, so the rating says how many of
 * those it rests on — that can differ from the opened count beside it.
 *
 * English and French rows of the same category stay separate rows; each
 * names its language under the title.
 */
// An issue row names where it sits: "Cards › Card top-up".
const ancestry = row =>
  [row.categoryLabel, row.featureLabel].filter(Boolean).join(' › ');

export const RankingTable = ({
  rows,
  loading,
  onSelect,
  emptyText,
  basis = 'OPENED',
  showAncestry,
}) => {
  const verb = basis === 'RESOLVED' ? 'resolved' : 'opened';
  const visible = (rows || []).slice(0, 10);
  return (
    <div className={styles.tableScroll}>
      <Table
        size="middle"
        className={styles.rankingTable}
        rowKey={row => `${row.id}-${row.language}-${row.product}`}
        loading={loading}
        pagination={false}
        dataSource={visible}
        locale={{emptyText: emptyText || `Nothing ${verb} in this period`}}
        onRow={
          onSelect
            ? row => ({
                onClick: () => onSelect(row),
                className: styles.clickableRow,
              })
            : undefined
        }
        columns={[
          {
            title: 'Topic',
            key: 'label',
            render: (_, row) => (
              <div className={styles.topicCell}>
                <span className={styles.topicName}>
                  {rankingLabel(row)}
                  {onSelect && <RightOutlined className={styles.drillIcon} />}
                </span>
                {showAncestry && ancestry(row) && (
                  <span className={styles.topicMeta}>{ancestry(row)}</span>
                )}
                <span className={styles.topicMeta}>
                  {[
                    row.language && languageLabel(row.language),
                    row.product && productLabel(row.product),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
            ),
          },
          {
            title: (
              <Explained
                text={basis === 'RESOLVED' ? TERMS.shareResolved : TERMS.share}>
                Tickets {verb}
              </Explained>
            ),
            key: 'count',
            width: 220,
            render: (_, row) => (
              <div className={styles.shareCell}>
                <div className={styles.shareLine}>
                  <span className={styles.shareCount}>
                    <strong>{formatCount(row.count)}</strong>{' '}
                    {row.count === 1 ? 'ticket' : 'tickets'}
                  </span>
                  <span className={styles.sharePercent}>
                    {Number(row.percentage || 0).toFixed(0)}%
                  </span>
                </div>
                {/* Width is the real share of the period, not scaled to the
                    top row, so 33% looks like a third. */}
                <div className={styles.shareTrack}>
                  <span
                    className={styles.shareFill}
                    style={{
                      width: `${Math.min(100, row.percentage || 0)}%`,
                    }}
                  />
                </div>
              </div>
            ),
          },
          {
            title: <Explained text={TERMS.change}>vs last period</Explained>,
            key: 'change',
            width: 120,
            render: (_, row) => {
              const change = formatChange(row);
              return (
                <span
                  className={`${styles.changeBadge} ${changeClass(change)}`}
                  title={`Previous period: ${formatCount(
                    row.previousCount,
                  )} tickets ${verb}`}>
                  {change}
                </span>
              );
            },
          },
          {
            title: <Explained text={TERMS.rankingRating}>Rating</Explained>,
            key: 'rating',
            width: 190,
            render: (_, row) => {
              const metrics = row.metrics || {};
              return (
                <div className={styles.twoLine}>
                  <span>
                    {metrics.averageRating === null ||
                    metrics.averageRating === undefined ? (
                      <span className={styles.subtle}>No ratings</span>
                    ) : (
                      formatAverageRating(metrics.averageRating)
                    )}
                  </span>
                  <span className={styles.smallNote}>
                    {formatCount(metrics.ratingCount || 0)} of{' '}
                    {formatCount(metrics.resolved || 0)} resolved rated
                    {metrics.resolved
                      ? ` · ${formatCoverage(metrics.ratingCoverage ?? 0)}`
                      : ''}
                  </span>
                </div>
              );
            },
          },
          {
            title: <Explained text={TERMS.turnaround}>Queue time</Explained>,
            key: 'turnaround',
            width: 170,
            render: (_, row) => (
              <span
                className={
                  row.metrics?.averageAgentSeconds === null ||
                  row.metrics?.averageAgentSeconds === undefined
                    ? styles.subtle
                    : undefined
                }>
                {formatDuration(row.metrics?.averageAgentSeconds)}
              </span>
            ),
          },
        ]}
      />
    </div>
  );
};
